import { useCallback, useEffect, useState, type ReactNode } from "react";
import {
  fetchParticipantDetail,
  viewKycScreenshot,
  viewParticipantCv,
  type ParticipantDetail,
  type ViewedFile,
} from "../../api/admin";
import { messageOf } from "../../lib/errors";
import { safeHref, shortDate } from "../../lib/format";
import { Avatar, Badge } from "./AdminUi";
import { FileViewer } from "./FileViewer";

const TRACK: Record<string, string> = {
  uiux: "UI/UX Design",
  graphic: "Graphic Design",
  video: "Video Production",
  digital: "Digital Marketing",
};
const KYC: Record<string, [string, "positive" | "warning" | "negative"]> = {
  verified: ["Verified", "positive"],
  pending: ["Awaiting approval", "warning"],
  notstarted: ["Not started", "negative"],
};
const VERDICT: Record<string, [string, "positive" | "warning" | "negative" | "neutral"]> = {
  ready: ["Ready to Go", "positive"],
  nearly: ["Nearly There", "warning"],
  pending: ["Waiting on peers", "neutral"],
  more: ["More Work Needed", "negative"],
};

interface ParticipantPanelProps {
  userId: string;
  /** "gig" opens on the gig summary; "finance" on the finance summary. */
  focus: "gig" | "finance";
  canDelete: boolean;
  onClose: () => void;
  onDelete: (userId: string, email: string) => void;
}

/** Slide-over with one participant: who they are, where they are, and their files (view only). */
export function ParticipantPanel({
  userId,
  focus,
  canDelete,
  onClose,
  onDelete,
}: ParticipantPanelProps) {
  const [detail, setDetail] = useState<ParticipantDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [viewer, setViewer] = useState<{
    title: string;
    subtitle: string;
    load: () => Promise<ViewedFile>;
  } | null>(null);

  useEffect(() => {
    let live = true;
    setDetail(null);
    setError(null);
    fetchParticipantDetail(userId)
      .then((d) => live && setDetail(d))
      .catch((e: unknown) => live && setError(messageOf(e)));
    return () => {
      live = false;
    };
  }, [userId]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && !viewer && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, viewer]);

  const closeViewer = useCallback(() => setViewer(null), []);
  const name = detail ? `${detail.gig.firstName} ${detail.gig.lastName}`.trim() : "";

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-[#231416]/30 print:hidden"
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={name ? `Participant: ${name}` : "Participant"}
        className="fixed inset-y-0 right-0 z-50 flex w-full max-w-[520px] flex-col bg-surface shadow-[-12px_0_40px_rgba(35,20,22,0.18)] print:hidden"
      >
        <div className="flex items-start gap-3 border-b border-border p-5">
          {detail ? <Avatar first={detail.gig.firstName} last={detail.gig.lastName} /> : null}
          <div className="min-w-0 flex-1">
            <div className="truncate text-[17px] font-bold">{name || "Loading..."}</div>
            {detail ? (
              <div className="truncate text-[13px] text-text-muted">
                {detail.gig.email}
                {detail.gig.participantId ? ` · ${detail.gig.participantId}` : ""}
              </div>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close participant"
            className="h-9 w-9 rounded-[9px] text-[18px] text-text-muted hover:bg-surface-high"
          >
            ✕
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-5">
          {error ? (
            <p role="alert" className="text-negative">
              {error}
            </p>
          ) : !detail ? (
            <div role="status" aria-label="Loading participant" className="flex flex-col gap-3">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-20 animate-pulse rounded-[12px] bg-surface-high" />
              ))}
            </div>
          ) : (
            <div className="flex flex-col gap-5">
              <div className="flex flex-wrap gap-2">
                {detail.gig.track ? (
                  <Badge tone="accent">{TRACK[detail.gig.track] ?? detail.gig.track}</Badge>
                ) : (
                  <Badge>No track yet</Badge>
                )}
                {detail.gig.stepsDone.includes(4) ? (
                  <Badge tone="positive">Work-Enabled</Badge>
                ) : null}
                {detail.finance.referredAt ? <Badge tone="info">Referred for finance</Badge> : null}
                {detail.gig.stalledDays != null ? (
                  <Badge tone="warning">Stalled {detail.gig.stalledDays} days</Badge>
                ) : null}
                <Badge>Joined {shortDate(detail.joinedAt)}</Badge>
              </div>

              {focus === "gig" && (detail.cvName || detail.platforms.length) ? (
                <Block title="Documents">
                  <div className="flex flex-col gap-2">
                    {detail.cvName ? (
                      <FileRow
                        label="CV"
                        detail={detail.cvName}
                        onView={() =>
                          setViewer({
                            title: `${name}: CV`,
                            subtitle: detail.cvName ?? "",
                            load: () => viewParticipantCv(userId),
                          })
                        }
                      />
                    ) : (
                      <FileRow label="CV" detail="Not uploaded yet" />
                    )}
                    {detail.platforms.map((p) => (
                      <FileRow
                        key={p.platformId}
                        label={`KYC: ${p.name}`}
                        detail={p.kycStatus ? KYC[p.kycStatus]?.[0] : "No status yet"}
                        badge={
                          p.kycStatus ? (
                            <Badge tone={KYC[p.kycStatus]?.[1]}>{KYC[p.kycStatus]?.[0]}</Badge>
                          ) : undefined
                        }
                        onView={
                          p.hasKycScreenshot
                            ? () =>
                                setViewer({
                                  title: `${name}: ${p.name} KYC`,
                                  subtitle: KYC[p.kycStatus ?? ""]?.[0] ?? "Screenshot",
                                  load: () => viewKycScreenshot(userId, p.platformId),
                                })
                            : undefined
                        }
                      />
                    ))}
                  </div>
                </Block>
              ) : null}

              {focus === "gig" ? (
                <Block title="Gig readiness">
                  <dl className="grid grid-cols-2 gap-3">
                    <Fact label="Bids counted" value={`${detail.gig.bidsCounted}/10`} />
                    <Fact
                      label="Bid readiness"
                      value={detail.gig.bidReadiness != null ? `${detail.gig.bidReadiness}%` : "–"}
                    />
                    <Fact
                      label="Quality score"
                      value={detail.gig.qualityPct != null ? `${detail.gig.qualityPct}%` : "–"}
                    />
                    <Fact
                      label="Verdict"
                      value={
                        detail.gig.verdict ? (
                          <Badge tone={VERDICT[detail.gig.verdict]?.[1]}>
                            {VERDICT[detail.gig.verdict]?.[0] ?? detail.gig.verdict}
                          </Badge>
                        ) : (
                          "–"
                        )
                      }
                    />
                    <Fact
                      label="Arrival audit"
                      value={detail.gig.auditScore != null ? `${detail.gig.auditScore}%` : "–"}
                    />
                    <Fact
                      label="Last sign-in"
                      value={detail.gig.lastLogin ? shortDate(detail.gig.lastLogin) : "–"}
                    />
                  </dl>
                  {detail.niche ? (
                    <p className="mb-0 mt-3 rounded-[10px] bg-surface-high p-3 text-[13.5px] italic">
                      “{detail.niche}”
                    </p>
                  ) : null}
                  {detail.portfolioUrl ? (
                    <p className="mb-0 mt-2 text-[13px]">
                      Portfolio:{" "}
                      <a
                        href={safeHref(detail.portfolioUrl)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="break-all text-accent underline"
                      >
                        {detail.portfolioUrl}
                      </a>
                    </p>
                  ) : null}
                </Block>
              ) : (
                <Block title="Access to Finance">
                  <dl className="grid grid-cols-2 gap-3">
                    <Fact label="Readiness" value={detail.finance.tier ?? "–"} />
                    <Fact
                      label="Referred"
                      value={
                        detail.finance.referredAt ? shortDate(detail.finance.referredAt) : "Not yet"
                      }
                    />
                    <Fact label="ID documents" value={`${detail.finance.idDocsReady} ready`} />
                    <Fact label="Money records" value={`${detail.finance.moneyDocsReady} ready`} />
                  </dl>
                  {detail.finance.services.length ? (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {detail.finance.services.map((s) => (
                        <Badge key={s}>{s}</Badge>
                      ))}
                    </div>
                  ) : null}
                </Block>
              )}

              <Block title={focus === "gig" ? "Gig pathway" : "Finance track"}>
                <Timeline items={focus === "gig" ? detail.gigTimeline : detail.financeTimeline} />
              </Block>
              <Block title={focus === "gig" ? "Finance track" : "Gig pathway"}>
                <Timeline
                  items={focus === "gig" ? detail.financeTimeline : detail.gigTimeline}
                  compact
                />
              </Block>
            </div>
          )}
        </div>

        {canDelete && detail ? (
          <div className="border-t border-border p-4">
            <button
              type="button"
              onClick={() => onDelete(userId, detail.gig.email)}
              className="text-[13px] font-semibold text-negative hover:underline"
            >
              Delete this participant and all their data
            </button>
          </div>
        ) : null}
      </aside>
      {viewer ? (
        <FileViewer
          title={viewer.title}
          subtitle={viewer.subtitle}
          load={viewer.load}
          onClose={closeViewer}
        />
      ) : null}
    </>
  );
}

function Block({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h3
        className="mb-2 text-[12px] font-bold uppercase tracking-[0.08em] text-text-muted"
        style={{ fontFamily: "var(--font-body)" }}
      >
        {title}
      </h3>
      {children}
    </section>
  );
}

function Fact({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="rounded-[10px] border border-border p-3">
      <dt className="text-[11.5px] text-text-muted">{label}</dt>
      <dd className="m-0 mt-0.5 text-[15px] font-bold tabular">{value}</dd>
    </div>
  );
}

function FileRow({
  label,
  detail,
  badge,
  onView,
}: {
  label: string;
  detail?: string | undefined;
  badge?: ReactNode;
  onView?: (() => void) | undefined;
}) {
  return (
    <div className="flex items-center gap-3 rounded-[10px] border border-border p-3">
      <span
        aria-hidden="true"
        className="inline-flex h-9 w-9 items-center justify-center rounded-[8px] bg-surface-high text-text-muted"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <path
            d="M14 3H6a2 2 0 00-2 2v14a2 2 0 002 2h12a2 2 0 002-2V9l-6-6zm0 0v6h6"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[14px] font-semibold">{label}</div>
        <div className="truncate text-[12.5px] text-text-muted">{detail}</div>
      </div>
      {badge}
      {onView ? (
        <button
          type="button"
          onClick={onView}
          className="shrink-0 rounded-[9px] border-[1.5px] border-accent px-3 py-1.5 text-[13px] font-bold text-accent hover:bg-accent-tint"
        >
          View
        </button>
      ) : null}
    </div>
  );
}

function Timeline({
  items,
  compact,
}: {
  items: { step: number; name: string; completedAt?: string | null }[];
  compact?: boolean;
}) {
  const next = items.find((item) => !item.completedAt)?.step;
  return (
    <ol className="m-0 list-none p-0">
      {items.map((item, index) => (
        <li key={item.step} className="relative flex gap-3 pb-3 last:pb-0">
          {index < items.length - 1 ? (
            <span
              aria-hidden="true"
              className={`absolute left-[11px] top-6 h-[calc(100%-18px)] w-0.5 ${item.completedAt ? "bg-accent" : "bg-border"}`}
            />
          ) : null}
          <span
            aria-hidden="true"
            className={`relative z-[1] inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${
              item.completedAt
                ? "bg-accent text-on-accent"
                : item.step === next
                  ? "border-2 border-accent bg-surface text-accent"
                  : "border border-border bg-surface text-text-muted"
            }`}
          >
            {item.completedAt ? "✓" : item.step}
          </span>
          <div
            className={`flex flex-1 items-baseline justify-between gap-2 ${compact ? "text-[13px]" : "text-[14px]"}`}
          >
            <span className={item.completedAt ? "font-semibold" : "text-text-muted"}>
              <span className="sr-only">
                {item.completedAt ? "Done: " : item.step === next ? "Current: " : "Not started: "}
              </span>
              {item.name}
            </span>
            <span className="shrink-0 text-[12px] text-text-muted tabular">
              {item.completedAt
                ? shortDate(item.completedAt)
                : item.step === next
                  ? "In progress"
                  : ""}
            </span>
          </div>
        </li>
      ))}
    </ol>
  );
}
